import {
  CONTAINER_ELEMENT_ID,
  DESKTOP_SIZE_TRANSITION_MS,
  FALLBACK_ELEMENT_ID,
  FALLBACK_CLOSE_LABEL,
  FALLBACK_MESSAGE_TEXT,
  IFRAME_ELEMENT_ID,
  IFRAME_EXPANDED_HEIGHT_PX,
  IFRAME_EXPANDED_WIDTH_PX,
  IFRAME_MAX_HEIGHT_PX,
  IFRAME_MIN_HEIGHT_PX,
  IFRAME_OFFSET_PX,
  IFRAME_OPEN_HEIGHT_PX,
  IFRAME_OPEN_WIDTH_PX,
  MOBILE_BACKDROP_ELEMENT_ID,
  MOBILE_BREAKPOINT_PX,
  SCRIPT_ATTR,
  WEBCHAT_UI_ORIGIN,
} from './constants';
import { WebChatInitError, type WebchatPosition } from './types';
import type { SourceTrackingParams } from '@servicetitan/web-chat-types';

// SETO-7699: data-webchat-position is always anchored to the bottom -- this only picks which
// horizontal side. Consumed by showIframe/showFallback below on desktop (mobile ignores it
// entirely, going full-screen). Same values as WEBCHAT_POSITIONS (constants.ts) and
// launcherButton.ts's own CORNER_STYLE; keep in sync if a third value is ever added.
const POSITION_SIDE: Record<WebchatPosition, 'left' | 'right'> = {
  right: 'right',
  left: 'left',
};

const CLOSED_IFRAME_STYLE =
  'position:fixed;bottom:0;right:0;width:0;height:0;border:0;z-index:2147483647;';

const CLOSED_FALLBACK_STYLE =
  'position:fixed;bottom:0;right:0;width:0;height:0;overflow:hidden;border:0;z-index:2147483647;';

// One step under the surface so it always paints behind the iframe/fallback (both max z-index).
// White matches web-chat-ui's composer surface, which is what sits directly above the strip this
// covers (see showMobileBackdrop). The host only knows the widget exists, not its theme.
const MOBILE_BACKDROP_STYLE = 'position:fixed;inset:0;background:#fff;z-index:2147483646;';

const DESKTOP_SIZE_TRANSITION = `width ${DESKTOP_SIZE_TRANSITION_MS}ms ease, height ${DESKTOP_SIZE_TRANSITION_MS}ms ease`;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// SETO-7278: shared by showIframe/showFallback so both switch between the floating-box and
// full-screen-mobile layouts identically -- keeps them visually interchangeable (see showFallback's
// own comment on why that matters) rather than each hand-rolling its own breakpoint check.
export function isMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
}

// Full-screen mobile box, shared by showIframe and showFallback so the two stay interchangeable.
//
// SETO-7337: vw/vh first as a fallback for browsers that don't understand dvh/dvw -- the dvh/dvw
// assignments below are simply no-ops there, leaving vw/vh in place. On iOS Safari, vh is
// measured against the largest possible viewport (as if its dynamic toolbar were hidden), so a
// position:fixed box pinned to all four edges with height:100vh overflows past the visible
// viewport whenever that toolbar is showing. dvh/dvw track the toolbar.
//
// SETO-7774: dvh does not track the on-screen keyboard. Mobile browsers shrink only the *visual*
// viewport for the keyboard and leave the layout viewport (and so this fixed box) at full
// height; iOS then pans the page to reveal the focused composer inside the iframe and the
// widget's header scrolls off the top. Only this host document can see that: inside the iframe,
// visualViewport always equals the iframe's own box (MDN), so web-chat-ui cannot fix it from
// within. When the host exposes visualViewport, size the box to it exactly -- top/left follow the
// pan, width/height follow the shrink -- and init.ts re-runs this on every visualViewport
// resize/scroll while open. The numbers are the browser's own; nothing here predicts a keyboard.
function fillMobileViewport(el: HTMLElement): void {
  showMobileBackdrop(el);
  el.style.borderRadius = '0';
  el.style.boxShadow = 'none';

  const viewport = window.visualViewport;
  if (viewport) {
    el.style.top = `${viewport.offsetTop}px`;
    el.style.left = `${viewport.offsetLeft}px`;
    el.style.bottom = '';
    el.style.right = '';
    el.style.width = `${viewport.width}px`;
    el.style.height = `${viewport.height}px`;
    return;
  }

  el.style.top = '0';
  el.style.left = '0';
  el.style.bottom = '0';
  el.style.right = '0';
  el.style.width = '100vw';
  el.style.height = '100vh';
  el.style.width = '100dvw';
  el.style.height = '100dvh';
}

// SETO-7774: sizing the mobile surface to the visual viewport leaves the rest of the layout
// viewport uncovered -- the strip under iOS Safari's translucent URL pill (between the composer
// and the keyboard) and under the status bar when the page is panned. Those bars blur whatever is
// behind them, so host-page content bleeds through. This sheet sits behind the surface and pins to
// the layout viewport (fixed inset:0), so the bars blur a plain surface instead. Mounted as the
// surface's previous sibling so it shares the tenant's static-container escape hatch; skipped for a
// detached surface. Removed, not hidden, on close so a closed widget never leaves a sheet over
// the page.
//
// review: AMattRiley -- init.ts re-applies the mobile box on every visualViewport resize/scroll, so
// a sheet already sitting in front of this surface is left alone instead of re-styled and re-inserted.
function showMobileBackdrop(surface: HTMLElement): void {
  if (!surface.parentNode) return;
  const existing = document.getElementById(MOBILE_BACKDROP_ELEMENT_ID);
  if (existing?.nextSibling === surface) return;
  const backdrop = existing ?? document.createElement('div');
  backdrop.id = MOBILE_BACKDROP_ELEMENT_ID;
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.style.cssText = MOBILE_BACKDROP_STYLE;
  surface.parentNode.insertBefore(backdrop, surface);
}

function hideMobileBackdrop(): void {
  document.getElementById(MOBILE_BACKDROP_ELEMENT_ID)?.remove();
}

// SETO-7661 (review: seanw1023): a desktop-classified viewport is not automatically big enough for
// the expanded box. IFRAME_EXPANDED_WIDTH_PX happens to equal MOBILE_BREAKPOINT_PX, so 481-495px
// reads as desktop yet cannot hold 480px plus the anchored edge's offset -- the box would hang off
// that edge. Same story vertically on a short window (< 736px). Fall back to the default box in
// that case rather than inventing an unnamed in-between size: expanded is a named box, and a
// 462x600 "expanded" state is neither of the two sizes the child asked about. The window resize
// listener in init.ts re-runs showIframe, so growing the window back picks the expanded box up
// again without the child having to re-send anything.
function expandedBoxFits(): boolean {
  return (
    window.innerWidth >= IFRAME_EXPANDED_WIDTH_PX + IFRAME_OFFSET_PX &&
    window.innerHeight >= IFRAME_EXPANDED_HEIGHT_PX + IFRAME_OFFSET_PX
  );
}

// SETO-7661 (review: seanw1023): what init.ts reports to the child over webchat:expand-availability.
// The child cannot work this out for itself -- inside a cross-origin iframe its own viewport is
// just the box we handed it (380px on desktop), and window.screen.width is the physical monitor,
// which says nothing about a narrowed or half-screen browser window sitting on it. Only this
// loader sees the host viewport, so only this loader can answer. False on mobile too: that layout
// is full-screen by definition, with nothing to enlarge into.
export function isExpandAvailable(): boolean {
  return !isMobileViewport() && expandedBoxFits();
}

// SETO-7661 (review: AMattRiley): the two named desktop boxes, shared by showIframe and
// showFallback. The fallback that replaces an expanded iframe (e.g. webchat:disabled while
// expanded) keeps the same box instead of snapping down to the default one.
const DEFAULT_BOX = { width: IFRAME_OPEN_WIDTH_PX, height: IFRAME_OPEN_HEIGHT_PX };
const EXPANDED_BOX = { width: IFRAME_EXPANDED_WIDTH_PX, height: IFRAME_EXPANDED_HEIGHT_PX };

// Amber, not red — matches the tone ServiceTitan already uses for tenant-facing
// error states elsewhere (e.g. scheduler's ErrorView icon), so a transient
// chat outage doesn't read as more alarming to a tenant's own customers than
// it is.
const FALLBACK_BADGE_COLOR = '#FFA037';

// No bootstrap fetch: the loader builds the iframe src directly from the
// build-time web-chat-ui origin plus tenantId/agentId, which together form the
// tenant's embeddable "public key" (see the UI Hosting Options doc). All
// origin/config resolution for the widget itself happens server-side in
// web-chat-ui, not here.
//
// SETO-7267: sourceTrackingParams (already stripped of empty values by
// collectSourceTrackingParams) are appended as query params too, so web-chat-ui has them from the
// very first paint, without waiting on the webchat:ready postMessage channel.
//
// Built via the URL/URLSearchParams Web APIs (universally available -- this runs in the tenant's
// own browser) rather than manual template-string concatenation, so query encoding is handled
// correctly by the platform instead of hand-rolled. tenantId/agentId are still explicitly
// encodeURIComponent'd before being placed in the relative path string -- URL's own parser does
// NOT re-escape a literal "/" inside a path segment, so without this a tenantId/agentId containing
// one could inject an extra path segment.
export function buildIframeUrl(
  tenantId: string,
  agentId: string,
  sourceTrackingParams: SourceTrackingParams = {},
): string {
  const url = new URL(
    `webchat/${encodeURIComponent(tenantId)}/${encodeURIComponent(agentId)}`,
    WEBCHAT_UI_ORIGIN,
  );
  for (const [key, value] of Object.entries(sourceTrackingParams)) {
    if (value) url.searchParams.set(key, value);
  }
  return url.toString();
}

export function createIframe(iframeUrl: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ELEMENT_ID;
  iframe.src = iframeUrl;
  iframe.title = 'Chat';
  // SETO-7346: allow programmatic focus on open (focus management).
  iframe.tabIndex = -1;
  iframe.style.cssText = CLOSED_IFRAME_STYLE;
  return iframe;
}

export interface ShowIframeOptions {
  // Which named box to paint: the default open box, or the larger expanded one
  // (webchat:expand-request). Ignored on mobile, which is always full-screen.
  expanded?: boolean;
  // SETO-7661 (review: seanw1023): opt in to the width/height CSS transition, rather than setting
  // it on every call. Only an expand/restore toggle animates between two visible boxes; open() and
  // the late-webchat:ready fallback recovery both come from a hidden 0x0 iframe (hideIframe clears
  // cssText), so a transition there would animate the box growing out of the corner instead of
  // snapping open -- and close() is instant, so it would not even be symmetric.
  animateSize?: boolean;
}

// Floating overlay on desktop (no full-page-lock modal mode, per SETO-7080 --
// this is a distinct, standard concern, not a reopening of that decision);
// full-screen below MOBILE_BREAKPOINT_PX (SETO-7278), where the same fixed-size
// box either overflows or reads as disproportionately tiny. close() collapses
// it back to 0x0 rather than just hiding it, so a closed widget never sits on
// top of the page intercepting clicks. The border-radius/shadow live here, not
// on web-chat-ui's own Card (SETO-7278) -- the iframe is the actual visible
// boundary now that the Card fills it exactly, so rounding has to happen at
// this layer or it just clips against the page's own background again.
export function showIframe(
  iframe: HTMLIFrameElement,
  position: WebchatPosition,
  { expanded = false, animateSize = false }: ShowIframeOptions = {},
): void {
  if (isMobileViewport()) {
    iframe.style.transition = 'none';
    fillMobileViewport(iframe);
    return;
  }

  hideMobileBackdrop();
  const side = POSITION_SIDE[position];
  const { width, height } = expanded && expandedBoxFits() ? EXPANDED_BOX : DEFAULT_BOX;
  iframe.style.transition =
    animateSize && !prefersReducedMotion() ? DESKTOP_SIZE_TRANSITION : 'none';
  iframe.style.top = '';
  iframe.style.bottom = `${IFRAME_OFFSET_PX}px`;
  iframe.style.left = side === 'left' ? `${IFRAME_OFFSET_PX}px` : '';
  iframe.style.right = side === 'right' ? `${IFRAME_OFFSET_PX}px` : '';
  iframe.style.width = `${width}px`;
  iframe.style.height = `${height}px`;
  iframe.style.borderRadius = '12px';
  iframe.style.boxShadow = '0 2px 8px rgba(26, 26, 26, 0.08)';
}

export function hideIframe(iframe: HTMLIFrameElement): void {
  hideMobileBackdrop();
  iframe.style.cssText = CLOSED_IFRAME_STYLE;
}

// Lets the child (web-chat-ui) fit the overlay to its own content via the
// webchat:resize message — width stays fixed, only height adjusts. Clamped
// defensively: the sender's origin is already validated by the time this
// runs, but a broken content height still shouldn't be able to produce an
// unusably large or invisible overlay. No-ops on a mobile viewport (SETO-7278)
// -- full-screen height is 100vh by definition there, not something per-message
// content should be shrinking.
export function setIframeHeight(iframe: HTMLIFrameElement, height: number): void {
  if (isMobileViewport()) return;
  const clampedHeight = Math.min(IFRAME_MAX_HEIGHT_PX, Math.max(IFRAME_MIN_HEIGHT_PX, height));
  iframe.style.height = `${clampedHeight}px`;
}

// Ports the container/attach pattern from scheduler-embed's
// attachWidgetIframeToClientWebsite.ts, including the data-static-container-id
// escape hatch. Unlike that widget, our iframe is already self-positioning
// (fixed, 0x0 until opened), so the default container needs no special
// hide-offscreen styling of its own — it's just an attach point.
//
// The optional fallback element (shown in place of the iframe if it never
// loads — see createFallback()) is mounted as a sibling in the same
// container, so both share the tenant's static-container escape hatch too.
export function attachIframe(
  iframe: HTMLIFrameElement,
  staticContainerId?: string,
  fallback?: HTMLElement,
): void {
  const attach = (): void => {
    // Guard against double-attach if init() somehow runs more than once on the same page.
    if (document.getElementById(IFRAME_ELEMENT_ID)) {
      return;
    }

    if (staticContainerId) {
      const container = document.getElementById(staticContainerId);
      if (!container) {
        throw new WebChatInitError(
          'STATIC_CONTAINER_MISSING',
          `web-chat-embed: could not find element with id "${staticContainerId}" for ${SCRIPT_ATTR.staticContainerId}`,
        );
      }
      container.appendChild(iframe);
      if (fallback) {
        container.appendChild(fallback);
      }
      return;
    }

    const container = document.createElement('div');
    container.id = CONTAINER_ELEMENT_ID;
    document.body.appendChild(container);
    container.appendChild(iframe);
    if (fallback) {
      container.appendChild(fallback);
    }
  };

  if (document.body && document.readyState !== 'loading') {
    attach();
  } else {
    document.addEventListener('DOMContentLoaded', attach);
  }
}

// Rendered in place of the iframe if it never posts webchat:ready within
// READY_TIMEOUT_MS (see init.ts) — a plain sibling element, since the loader
// has no way to write into the iframe's own (cross-origin) document. A small
// badge + message, built with plain DOM calls (no innerHTML) since this is a
// script embedded on arbitrary third-party pages.
export function createFallback(onClose?: () => void): HTMLDivElement {
  const fallback = document.createElement('div');
  fallback.id = FALLBACK_ELEMENT_ID;
  // SETO-7346: allow programmatic focus when the fallback is the open surface.
  fallback.tabIndex = -1;
  fallback.style.cssText = CLOSED_FALLBACK_STYLE;

  // Host-page sibling of the iframe — cannot reuse ChatHeader's close control
  // (that lives in the cross-origin document). Matches OriginMismatchPanel's
  // top-right X so every error surface has the same dismiss affordance.
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', FALLBACK_CLOSE_LABEL);
  closeButton.textContent = '×';
  closeButton.style.cssText =
    'position:absolute;top:8px;right:8px;margin:0;padding:4px 10px;border:0;' +
    'background:transparent;color:#040404;font:20px/1 system-ui,sans-serif;cursor:pointer;';
  if (onClose) {
    closeButton.addEventListener('click', onClose);
  }

  const badge = document.createElement('div');
  badge.textContent = '!';
  badge.setAttribute('aria-hidden', 'true');
  badge.style.cssText =
    `flex:none;width:28px;height:28px;border-radius:50%;background:${FALLBACK_BADGE_COLOR};` +
    'color:#fff;font:bold 16px/28px system-ui,sans-serif;text-align:center;';

  // SETO-7346: announce the error when the fallback becomes visible (role=alert on the
  // message node so the root can still take role=dialog while open without a role clash).
  const message = document.createElement('div');
  message.setAttribute('role', 'alert');
  message.textContent = FALLBACK_MESSAGE_TEXT;

  fallback.append(closeButton, badge, message);
  return fallback;
}

// Mirrors showIframe: same floating-overlay box (or full-screen on mobile,
// SETO-7278), sized/positioned identically so swapping between iframe and
// fallback is visually seamless. Unlike the plain-text version this replaced,
// this needs its own visible card chrome (border/shadow/radius) — a bare
// white box was indistinguishable from a white host page behind it. All
// corners round now (SETO-7278): with the offset, the box floats clear of the
// viewport edge on both supported sides, unlike before when it was flush
// against bottom-right. Colors/spacing pulled from Anvil2's current tokens
// (hammer repo, not this loader's own dependency) and scheduler's ErrorView
// precedent (amber, not red, for tenant-facing failures). Takes the same expanded flag as
// showIframe (never animated: the fallback is a swap, not a resize).
export function showFallback(
  fallback: HTMLDivElement,
  position: WebchatPosition,
  { expanded = false }: Pick<ShowIframeOptions, 'expanded'> = {},
): void {
  if (isMobileViewport()) {
    fillMobileViewport(fallback);
  } else {
    hideMobileBackdrop();
    const side = POSITION_SIDE[position];
    const { width, height } = expanded && expandedBoxFits() ? EXPANDED_BOX : DEFAULT_BOX;
    fallback.style.top = '';
    fallback.style.left = side === 'left' ? `${IFRAME_OFFSET_PX}px` : '';
    fallback.style.bottom = `${IFRAME_OFFSET_PX}px`;
    fallback.style.right = side === 'right' ? `${IFRAME_OFFSET_PX}px` : '';
    fallback.style.width = `${width}px`;
    fallback.style.height = `${height}px`;
    fallback.style.borderRadius = '12px';
    fallback.style.boxShadow = '0 2px 8px rgba(26, 26, 26, 0.08)';
  }
  fallback.style.boxSizing = 'border-box';
  fallback.style.display = 'flex';
  fallback.style.flexDirection = 'column';
  fallback.style.alignItems = 'center';
  fallback.style.justifyContent = 'center';
  fallback.style.gap = '12px';
  fallback.style.padding = '24px';
  fallback.style.textAlign = 'center';
  fallback.style.background = '#fff';
  fallback.style.border = '1px solid #e6e6e6';
  fallback.style.color = '#040404';
  fallback.style.font =
    "16px/1.5 'Nunito Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
}

export function hideFallback(fallback: HTMLDivElement): void {
  hideMobileBackdrop();
  fallback.style.cssText = CLOSED_FALLBACK_STYLE;
}
